// apps/web/src/components/platform/ResourceDetail.tsx
//
// API-backed detail + edit form for a single resource. Backed by the
// now-complete /api/v1 surface. `RESOURCE_PROFILES` maps a URL group
// (first splat segment) → the API path template, the title field, and
// the fields to render/edit. Unknown groups fall back to a summary card
// so the full 896-route depth still renders.

import { useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';

type FieldType = 'text' | 'number' | 'select' | 'textarea' | 'date';

interface FieldDef {
  prop: string;
  label: string;
  type?: FieldType;
  options?: string[];
  editable?: boolean;
}

interface ResourceProfile {
  apiPath: string;
  titleProp: string;
  fields: FieldDef[];
}

/**
 * Field maps. `prop` matches the API JSON keys the backend returns.
 * `editable` fields appear in the edit form and are sent to PATCH.
 */
export const RESOURCE_PROFILES: Record<string, ResourceProfile> = {
  campaigns: {
    apiPath: '/api/v1/campaigns/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'PLANNED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED', 'ARCHIVED'] },
      { prop: 'objective', label: 'Objective', editable: true, type: 'textarea' },
      { prop: 'startAt', label: 'Start', type: 'date' },
      { prop: 'endAt', label: 'End', type: 'date' },
      { prop: 'budgetAmount', label: 'Budget', type: 'number' },
      { prop: 'budgetCurrency', label: 'Currency' },
      { prop: 'brandId', label: 'Brand' },
      { prop: 'tenantId', label: 'Tenant' },
    ],
  },
  creators: {
    apiPath: '/api/v1/creators/:id',
    titleProp: 'displayName',
    fields: [
      { prop: 'displayName', label: 'Display name', editable: true },
      { prop: 'creatorType', label: 'Type', editable: true, type: 'select', options: ['INDIVIDUAL', 'CREATOR_BUSINESS', 'CREATOR_COLLECTIVE', 'MEDIA_CREATOR', 'VIRTUAL_CREATOR', 'OTHER'] },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'ARCHIVED'] },
      { prop: 'partyId', label: 'Party' },
    ],
  },
  content: {
    apiPath: '/api/v1/content/:id',
    titleProp: 'title',
    fields: [
      { prop: 'title', label: 'Title', editable: true },
      { prop: 'contentType', label: 'Type', editable: true, type: 'select', options: ['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO', 'OTHER'] },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PUBLISHED', 'ARCHIVED'] },
      { prop: 'campaignId', label: 'Campaign' },
      { prop: 'creatorProfileId', label: 'Creator' },
      { prop: 'externalContentId', label: 'External id' },
    ],
  },
  deliverables: {
    apiPath: '/api/v1/deliverables/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['PENDING', 'IN_PROGRESS', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PUBLISHED', 'COMPLETED', 'CANCELLED'] },
      { prop: 'contentType', label: 'Content type' },
      { prop: 'quantity', label: 'Quantity', type: 'number' },
      { prop: 'dueAt', label: 'Due', type: 'date' },
      { prop: 'campaignId', label: 'Campaign' },
    ],
  },
  brands: {
    apiPath: '/api/v1/brands/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'ARCHIVED'] },
      { prop: 'organisationId', label: 'Organisation' },
    ],
  },
  programmes: {
    apiPath: '/api/v1/programmes/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'ARCHIVED', 'CANCELLED'] },
      { prop: 'brandId', label: 'Brand' },
      { prop: 'startAt', label: 'Start', type: 'date' },
      { prop: 'endAt', label: 'End', type: 'date' },
    ],
  },
  agreements: {
    apiPath: '/api/v1/agreements/:id',
    titleProp: 'agreementType',
    fields: [
      { prop: 'agreementType', label: 'Type' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'PENDING_SIGNATURE', 'ACTIVE', 'EXPIRED', 'TERMINATED', 'CANCELLED'] },
      { prop: 'effectiveFrom', label: 'Effective from', type: 'date' },
      { prop: 'effectiveTo', label: 'Effective to', type: 'date' },
    ],
  },
  offers: {
    apiPath: '/api/v1/offers/:id',
    titleProp: 'id',
    fields: [
      { prop: 'campaignId', label: 'Campaign' },
      { prop: 'participantId', label: 'Participant' },
      { prop: 'proposedAmount', label: 'Proposed amount', type: 'number' },
      { prop: 'currency', label: 'Currency' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['PENDING', 'ACCEPTED', 'DECLINED', 'COUNTERED', 'EXPIRED', 'WITHDRAWN'] },
      { prop: 'expiresAt', label: 'Expires', type: 'date' },
    ],
  },
  invoices: {
    apiPath: '/api/v1/invoices/:id',
    titleProp: 'invoiceNumber',
    fields: [
      { prop: 'invoiceNumber', label: 'Number' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'VOID', 'CANCELLED'] },
      { prop: 'totalAmount', label: 'Total', type: 'number' },
      { prop: 'currency', label: 'Currency' },
      { prop: 'payerPartyId', label: 'Payer' },
      { prop: 'payeePartyId', label: 'Payee' },
      { prop: 'dueAt', label: 'Due', type: 'date' },
    ],
  },
  payments: {
    apiPath: '/api/v1/payments/:id',
    titleProp: 'id',
    fields: [
      { prop: 'amount', label: 'Amount', type: 'number' },
      { prop: 'currency', label: 'Currency' },
      { prop: 'paymentMethod', label: 'Method' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['PENDING', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'CANCELLED', 'REFUNDED'] },
      { prop: 'invoiceId', label: 'Invoice' },
      { prop: 'scheduledAt', label: 'Scheduled', type: 'date' },
    ],
  },
  settlements: {
    apiPath: '/api/v1/settlements/:id',
    titleProp: 'id',
    fields: [
      { prop: 'grossAmount', label: 'Gross', type: 'number' },
      { prop: 'feesAmount', label: 'Fees', type: 'number' },
      { prop: 'taxAmount', label: 'Tax', type: 'number' },
      { prop: 'netAmount', label: 'Net', type: 'number' },
      { prop: 'currency', label: 'Currency' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['PENDING', 'PROCESSING', 'SETTLED', 'FAILED', 'CANCELLED'] },
    ],
  },
  issues: {
    apiPath: '/api/v1/issues/:id',
    titleProp: 'title',
    fields: [
      { prop: 'title', label: 'Title', editable: true },
      { prop: 'description', label: 'Description', editable: true, type: 'textarea' },
      { prop: 'issueType', label: 'Type' },
      { prop: 'priority', label: 'Priority', editable: true, type: 'select', options: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'BLOCKED'] },
      { prop: 'assigneeUserId', label: 'Assignee' },
    ],
  },
  disputes: {
    apiPath: '/api/v1/disputes/:id',
    titleProp: 'title',
    fields: [
      { prop: 'title', label: 'Title', editable: true },
      { prop: 'description', label: 'Description', editable: true, type: 'textarea' },
      { prop: 'disputeType', label: 'Type' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['OPEN', 'IN_MEDIATION', 'ESCALATED', 'RESOLVED', 'CLOSED'] },
    ],
  },
  documents: {
    apiPath: '/api/v1/documents/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'description', label: 'Description', editable: true, type: 'textarea' },
      { prop: 'documentType', label: 'Type' },
      { prop: 'fileSize', label: 'Size (bytes)', type: 'number' },
    ],
  },
  parties: {
    apiPath: '/api/v1/parties/:id',
    titleProp: 'displayName',
    fields: [
      { prop: 'displayName', label: 'Display name', editable: true },
      { prop: 'partyType', label: 'Type' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'ARCHIVED'] },
    ],
  },
  persons: {
    apiPath: '/api/v1/persons/:id',
    titleProp: 'firstName',
    fields: [
      { prop: 'firstName', label: 'First name', editable: true },
      { prop: 'lastName', label: 'Last name', editable: true },
      { prop: 'email', label: 'Email', editable: true },
      { prop: 'phone', label: 'Phone', editable: true },
    ],
  },
  organisations: {
    apiPath: '/api/v1/organisations/:id',
    titleProp: 'legalName',
    fields: [
      { prop: 'legalName', label: 'Legal name', editable: true },
      { prop: 'tradingName', label: 'Trading name', editable: true },
      { prop: 'registrationNumber', label: 'Registration', editable: true },
      { prop: 'taxIdentifier', label: 'Tax id', editable: true },
      { prop: 'countryCode', label: 'Country', editable: true },
    ],
  },
  'social-accounts': {
    apiPath: '/api/v1/social-accounts/:id',
    titleProp: 'handle',
    fields: [
      { prop: 'platform', label: 'Platform' },
      { prop: 'handle', label: 'Handle', editable: true },
      { prop: 'displayName', label: 'Display name', editable: true },
      { prop: 'creatorProfileId', label: 'Creator' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'INACTIVE', 'SUSPENDED'] },
      { prop: 'connectedAt', label: 'Connected', type: 'date' },
    ],
  },
  webhooks: {
    apiPath: '/api/v1/webhooks/:id',
    titleProp: 'url',
    fields: [
      { prop: 'url', label: 'URL', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'DISABLED'] },
    ],
  },
  subscriptions: {
    apiPath: '/api/v1/subscriptions/:id',
    titleProp: 'plan',
    fields: [
      { prop: 'plan', label: 'Plan', editable: true },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'PAUSED'] },
      { prop: 'renewsAt', label: 'Renews', type: 'date' },
    ],
  },
  roles: {
    apiPath: '/api/v1/roles/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'code', label: 'Code' },
      { prop: 'scopeType', label: 'Scope' },
      { prop: 'isSystem', label: 'System' },
    ],
  },
  'api-keys': {
    apiPath: '/api/v1/api-keys/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'keyPrefix', label: 'Key prefix' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'REVOKED'] },
      { prop: 'lastUsedAt', label: 'Last used', type: 'date' },
    ],
  },
  exports: {
    apiPath: '/api/v1/exports/:id',
    titleProp: 'exportType',
    fields: [
      { prop: 'exportType', label: 'Type' },
      { prop: 'format', label: 'Format' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['PENDING', 'READY', 'CANCELLED'] },
      { prop: 'completedAt', label: 'Completed', type: 'date' },
    ],
  },
  reports: {
    apiPath: '/api/v1/reports/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'reportType', label: 'Type' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['DRAFT', 'READY'] },
      { prop: 'generatedAt', label: 'Generated', type: 'date' },
    ],
  },
  consents: {
    apiPath: '/api/v1/consents/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'consentType', label: 'Type' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'INACTIVE', 'EXPIRED', 'WITHDRAWN'] },
      { prop: 'expiresAt', label: 'Expires', type: 'date' },
    ],
  },
  rights: {
    apiPath: '/api/v1/rights/:id',
    titleProp: 'rightType',
    fields: [
      { prop: 'rightType', label: 'Type' },
      { prop: 'contentAssetId', label: 'Content asset' },
      { prop: 'partyId', label: 'Party' },
      { prop: 'territoryCode', label: 'Territory', editable: true },
      { prop: 'startsAt', label: 'Starts', type: 'date' },
      { prop: 'endsAt', label: 'Ends', type: 'date' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'REVOKED', 'EXPIRED'] },
    ],
  },
  teams: {
    apiPath: '/api/v1/teams/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'workspaceId', label: 'Workspace' },
    ],
  },
  workspaces: {
    apiPath: '/api/v1/workspaces/:id',
    titleProp: 'name',
    fields: [
      { prop: 'name', label: 'Name', editable: true },
      { prop: 'code', label: 'Code' },
      { prop: 'status', label: 'Status', editable: true, type: 'select', options: ['ACTIVE', 'ARCHIVED'] },
    ],
  },
  files: {
    apiPath: '/api/v1/files/:id',
    titleProp: 'filename',
    fields: [
      { prop: 'filename', label: 'Filename', editable: true },
      { prop: 'mimeType', label: 'Mime type' },
      { prop: 'sizeBytes', label: 'Size (bytes)', type: 'number' },
      { prop: 'checksum', label: 'Checksum' },
    ],
  },
};

function humanize(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

interface ResourceDetailProps {
  group: string;
  id: string;
}

function FieldControl({
  def,
  value,
  onChange,
}: {
  def: FieldDef;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  if (def.type === 'select' && def.options) {
    return (
      <select className="form-control" value={value} onChange={(e) => onChange(e.target.value)}>
        {def.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (def.type === 'textarea') {
    return <textarea className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />;
}

export function ResourceDetail({ group, id }: ResourceDetailProps): JSX.Element | null {
  const profile = RESOURCE_PROFILES[group];
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get(profile.apiPath.replace(':id', id))
      .then((res) => {
        if (cancelled) return;
        const record = res.data as Record<string, unknown>;
        setData(record);
        const initial: Record<string, string> = {};
        for (const f of profile.fields) {
          const v = record[f.prop];
          if (v !== undefined && v !== null) initial[f.prop] = String(v);
        }
        setDraft(initial);
      })
      .catch((err) => {
        if (!cancelled) setError((err as { message?: string })?.message ?? 'Failed to load resource.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, id]);

  if (!profile) {
    return <FallbackDetail group={group} id={id} />;
  }

  if (loading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const title = data[profile.titleProp];

  const submit = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      for (const f of profile.fields) {
        if (!f.editable) continue;
        const v = draft[f.prop];
        body[f.prop] = f.type === 'number' ? Number(v) : v;
      }
      await apiClient.patch(profile.apiPath.replace(':id', id), body);
      const res = await apiClient.get(profile.apiPath.replace(':id', id));
      setData(res.data as Record<string, unknown>);
      setEditing(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const editableFields = profile.fields.filter((f) => f.editable);
  const readOnlyFields = profile.fields.filter((f) => !f.editable);

  return (
    <div className="resource-detail">
      <div className="header">
        <h2>{title ? humanize(title) : `${group} ${id.slice(0, 8)}`}</h2>
        <div className="actions">
          {!editing ? (
            <button type="button" className="btn btn-primary" onClick={() => { setEditing(true); setError(null); }}>
              Edit
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </>
          )}
          <button type="button" className="btn btn-outline" onClick={() => window.history.back()}>Back</button>
        </div>
      </div>

      {saved && <p className="notice success">Saved.</p>}
      {error && <div className="error">{error}</div>}

      <div className="manager-card detail-grid">
        {readOnlyFields.map((f) => (
          <div className="detail-field" key={f.prop}>
            <span className="detail-label">{f.label}</span>
            <span className="detail-value">{humanize(data[f.prop])}</span>
          </div>
        ))}
        {editableFields.map((f) => (
          <div className="detail-field" key={f.prop}>
            <span className="detail-label">{f.label}</span>
            {editing ? (
              <FieldControl def={f} value={draft[f.prop] ?? ''} onChange={(v) => setDraft((d) => ({ ...d, [f.prop]: v }))} />
            ) : (
              <span className="detail-value">{humanize(data[f.prop])}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FallbackDetail({ group, id }: { group: string; id: string }): JSX.Element {
  return (
    <div className="generic-detail">
      <h2 className="capitalize">{group}</h2>
      <p className="muted">Detail view for {id || 'this resource'}.</p>
      <div className="manager-card">
        <p>{group} {id ? `(${id.slice(0, 8)})` : ''}</p>
        <div className="actions">
          <button type="button" className="btn btn-primary" onClick={() => window.history.back()}>Back</button>
        </div>
      </div>
    </div>
  );
}